import React from 'react';

import type * as api from '@gitbook/api-client';
import type { HiveClient } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { assertNever } from '@gitbook/util-ts';

import { useNavigation } from '../navigation';

/**
 * Hook to simplify navigation to a content location.
 */
export function useNavigateToContentLocation(): (
    contentLocation: api.ContentLocation
) => Promise<void> {
    const { navigate } = useNavigation();

    const hiveClient = useHiveClient();

    // use the current revision as a fallback in case the content location doesn't have a revision
    const { revision: currentRevision } = useHiveRevision();

    return React.useCallback(
        async (contentLocation: api.ContentLocation) => {
            switch (contentLocation.kind) {
                case 'page': {
                    const { space, page, versionContext, internalLocation } = contentLocation;
                    if (page.type === hiveCore.RevisionPageType.Link) {
                        return;
                    }

                    const revision = await getContentLocationRevision(
                        currentRevision,
                        hiveClient,
                        space,
                        versionContext
                    );

                    if (!revision) {
                        return;
                    }

                    const pagePath = hiveCore.getAbsolutePagePathInRevision(
                        revision,
                        page.id,
                        false
                    );

                    if (!pagePath) {
                        // This means that the current revision doesn't contain that page (anymore).
                        // We can fetch the contentLocation.revision and navigate to a different
                        // revision but in the backlinks use cases that's needed as we shouldn'thave
                        // any stale backlinks.
                        return;
                    }

                    navigate({
                        screen: (prevScreen) => {
                            const isCurrentSpace =
                                prevScreen.type === 'space' && space.id === prevScreen.space;

                            if (isCurrentSpace) {
                                if (
                                    'sideSheet' in prevScreen &&
                                    prevScreen.sideSheet?.type === 'audits' &&
                                    internalLocation?.type === 'node'
                                ) {
                                    return {
                                        ...prevScreen,
                                        page: pagePath,
                                        sideSheet: {
                                            ...prevScreen.sideSheet,
                                            node: internalLocation.node,
                                        },
                                    };
                                }

                                return {
                                    ...prevScreen,
                                    page: pagePath,
                                    hash:
                                        internalLocation?.type === 'anchor'
                                            ? internalLocation.anchor
                                            : undefined,
                                };
                            }

                            return {
                                type: 'space',
                                space: space.id,
                                page: pagePath,
                                hash:
                                    internalLocation?.type === 'anchor'
                                        ? internalLocation.anchor
                                        : undefined,
                            };
                        },
                    });

                    break;
                }
                case 'reusable-content': {
                    navigate({
                        screen: {
                            type: 'space',
                            reusableContent: contentLocation.reusableContent.id,
                            space: contentLocation.space.id,
                        },
                    });

                    break;
                }

                case 'file': {
                    // Nothing to do here, we don't support navigating to files
                    break;
                }
                case 'collection': {
                    const { collection } = contentLocation;

                    navigate({
                        screen: { type: 'collection', collection: collection.id },
                    });

                    break;
                }
                case 'space': {
                    const { space } = contentLocation;

                    navigate({
                        screen: { type: 'space', space: space.id },
                    });

                    break;
                }
                case 'user': {
                    const { user } = contentLocation;

                    navigate({
                        screen: { type: 'user', user: user.id },
                    });

                    break;
                }
                case 'url': {
                    // skip for now, handle when actually needed
                    break;
                }
                case 'openapi': {
                    const { openAPISpec } = contentLocation;

                    navigate({
                        screen: { type: 'openapi_spec', spec: openAPISpec.slug },
                    });

                    break;
                }
                default:
                    assertNever(contentLocation);
            }
        },
        [currentRevision, hiveClient.store, navigate]
    );
}

/**
 * Get the revision for a content location.
 */
async function getContentLocationRevision(
    currentRevision: hiveCore.Revision | null,
    hiveClient: HiveClient,
    contentLocationSpace: api.Space,
    contentLocationVersionContext?:
        | api.ContentLocationRevisionContext
        | api.ContentLocationChangeRequestContext
): Promise<hiveCore.Revision | null | undefined> {
    // if same space, we can use the current revision
    if (currentRevision && contentLocationSpace.id === currentRevision?.contentId) {
        return currentRevision;
    }

    // if different space, we need to fetch the revision depending on the version
    const contentLocationRevisionId = (() => {
        switch (contentLocationVersionContext?.type) {
            case 'revision':
                return contentLocationVersionContext.revision.id;
            case 'changeRequest':
                return contentLocationVersionContext.changeRequest.revision;
            default:
                return contentLocationSpace.revision;
        }
    })();

    return hiveClient.store.getRevision(contentLocationSpace.id, contentLocationRevisionId);
}

export function extractContentLocationInfo(locationRef?: api.ContentLocation): {
    id: string;
    text: string;
    icon: SVGIconComponent;
} {
    if (!locationRef) {
        return {
            id: Math.random().toString(),
            text: 'Inaccessible content',
            icon: Icon.PageLink,
        };
    }

    switch (locationRef.kind) {
        case 'page':
            return {
                id: locationRef.page.id,
                text: locationRef.page.title,
                icon: Icon.PageLink,
            };
        case 'file':
            return {
                id: locationRef.file.id,
                text: locationRef.file.name,
                icon: Icon.FileWord,
            };
        case 'collection':
            return {
                id: locationRef.collection.id,
                text: locationRef.collection.title,
                icon: Icon.Collection,
            };
        case 'space':
            return {
                id: locationRef.space.id,
                text: locationRef.space.title,
                icon: Icon.SpaceLink,
            };
        case 'user':
            return {
                id: locationRef.user.id,
                text: locationRef.user.displayName,
                icon: Icon.User,
            };
        case 'url':
            return {
                id: locationRef.url,
                text: locationRef.url,
                icon: Icon.Link,
            };
        case 'reusable-content':
            return {
                id: locationRef.reusableContent.id,
                text: locationRef.reusableContent.title,
                icon: Icon.ReusableContent,
            };
        case 'openapi': {
            return {
                id: locationRef.openAPISpec.id,
                text: locationRef.openAPISpec.slug,
                icon: Icon.OpenAPI,
            };
        }
        default:
            assertNever(locationRef);
    }
}
